/**
 * GlobalBackground.jsx
 *
 * Drop this ONCE into App.jsx (or your root layout) as the very first child.
 * It sits fixed behind everything — no section needs its own background anymore.
 *
 * Usage in App.jsx:
 *   import GlobalBackground from "./GlobalBackground";
 *   export default function App() {
 *     return (
 *       <>
 *         <GlobalBackground />
 *         <LandingPage />
 *       </>
 *     );
 *   }
 *
 * Then in every section component, simply remove or set to "transparent"
 * whatever background / background-color they currently declare.
 * The only exception is intentional dark sections (e.g. FreeVetAssist CTA)
 * — keep those, they'll layer on top of this base correctly.
 */

export default function GlobalBackground() {
  return (
    <>
      <style>{`
        /* ── reset so the page itself is transparent ── */
        html, body, #root {
          background: transparent !important;
        }

        /* ── the fixed canvas ── */
        .gbl-bg {
          position: fixed;
          inset: 0;
          z-index: -1;
          pointer-events: none;
          overflow: hidden;

          /* ── base gradient: top-to-bottom colour story ──
             mint-green top → clean white centre → soft sky-blue lower → pale mint bottom */
          background:
            linear-gradient(
              180deg,
              #edfcf2 0%,
              #f4fbff 22%,
              transparent 45%,
              #f0f7ff 68%,
              #edfaf4 100%
            );
        }

        /* ── large ambient blobs ── */
        .gbl-blob {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
        }

        /* Top-left — green bloom (hero area) */
        .gbl-blob-1 {
          width: 75vw;
          height: 65vh;
          top: -18vh;
          left: -20vw;
          background: radial-gradient(
            ellipse at center,
            rgba(34, 197, 94, 0.10) 0%,
            rgba(34, 197, 94, 0.04) 45%,
            transparent 70%
          );
        }

        /* Top-right — blue-indigo (hero / features) */
        .gbl-blob-2 {
          width: 60vw;
          height: 55vh;
          top: -10vh;
          right: -18vw;
          background: radial-gradient(
            ellipse at center,
            rgba(41, 92, 172, 0.08) 0%,
            rgba(41, 92, 172, 0.03) 50%,
            transparent 72%
          );
        }

        /* Centre — teal (middle sections) */
        .gbl-blob-3 {
          width: 70vw;
          height: 50vh;
          top: 38vh;
          left: 50%;
          transform: translateX(-50%);
          background: radial-gradient(
            ellipse at center,
            rgba(72, 194, 169, 0.07) 0%,
            rgba(72, 194, 169, 0.02) 55%,
            transparent 75%
          );
        }

        /* Lower-left — blue (dashboard / network area) */
        .gbl-blob-4 {
          width: 55vw;
          height: 45vh;
          top: 58vh;
          left: -15vw;
          background: radial-gradient(
            ellipse at center,
            rgba(41, 92, 172, 0.06) 0%,
            rgba(41, 92, 172, 0.02) 55%,
            transparent 72%
          );
        }

        /* Lower-right — green (footer area) */
        .gbl-blob-5 {
          width: 60vw;
          height: 50vh;
          bottom: -15vh;
          right: -15vw;
          background: radial-gradient(
            ellipse at center,
            rgba(34, 197, 94, 0.08) 0%,
            rgba(34, 197, 94, 0.03) 50%,
            transparent 70%
          );
        }

        /* ── subtle dot grid ──
           fades out after the hero so it doesn't overwhelm long-scroll sections */
        .gbl-grid {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(
            circle,
            rgba(41, 92, 172, 0.13) 1px,
            transparent 1px
          );
          background-size: 36px 36px;
          mask-image: linear-gradient(
            180deg,
            rgba(0,0,0,0.55) 0%,
            rgba(0,0,0,0.18) 35%,
            rgba(0,0,0,0) 65%
          );
          -webkit-mask-image: linear-gradient(
            180deg,
            rgba(0,0,0,0.55) 0%,
            rgba(0,0,0,0.18) 35%,
            rgba(0,0,0,0) 65%
          );
          pointer-events: none;
        }

        /* ── very faint noise grain for depth ──
           uses an inline SVG data-URI turbulence filter */
        .gbl-noise {
          position: absolute;
          inset: 0;
          opacity: 0.018;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
          background-repeat: repeat;
          background-size: 256px 256px;
          pointer-events: none;
        }
      `}</style>

      <div className="gbl-bg" aria-hidden="true">
        {/* Ambient colour blobs */}
        <div className="gbl-blob gbl-blob-1" />
        <div className="gbl-blob gbl-blob-2" />
        <div className="gbl-blob gbl-blob-3" />
        <div className="gbl-blob gbl-blob-4" />
        <div className="gbl-blob gbl-blob-5" />

        {/* Dot grid overlay */}
        <div className="gbl-grid" />

        {/* Grain texture */}
        <div className="gbl-noise" />
      </div>
    </>
  );
}
